import React, { useEffect, useState } from 'react';
import { generateQuestionnaire } from '../utils/openaiHelpers';
import { Spinner } from './ui/Spinner';
interface QuestionnaireProps {
  clientData: any;
  onSubmit: (responses: any) => void;
}
export const Questionnaire: React.FC<QuestionnaireProps> = ({
  clientData,
  onSubmit
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState<any>(null);
  const [activeSection, setActiveSection] = useState('sales');
  const [responses, setResponses] = useState({
    sales: {},
    marketing: {},
    operations: {},
    hr: {},
    finance: {}
  });
  const sections = [{
    id: 'sales',
    label: 'Sales',
    color: 'blue'
  }, {
    id: 'marketing',
    label: 'Marketing',
    color: 'purple'
  }, {
    id: 'operations',
    label: 'Operations',
    color: 'teal'
  }, {
    id: 'hr',
    label: 'Human Resources',
    color: 'orange'
  }, {
    id: 'finance',
    label: 'Finance',
    color: 'green'
  }];
  // Fetch questions when component mounts
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const questionsData = await generateQuestionnaire(clientData);
        setQuestions(questionsData);
      } catch (err) {
        setError('Failed to generate questionnaire. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [clientData]);
  const handleResponseChange = (section: string, questionId: string, value: number) => {
    setResponses(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [questionId]: value
      }
    }));
  };
  const isAllSectionsCompleted = () => {
    if (!questions) return false;
    return sections.every(section => {
      const sectionQuestions = questions[section.id];
      if (!sectionQuestions || sectionQuestions.length === 0) return true;
      return sectionQuestions.every(q => responses[section.id][q.id] !== undefined);
    });
  };
  const getSectionCompletionPercentage = (section: string) => {
    if (!questions || !questions[section] || questions[section].length === 0) return 0;
    const totalQuestions = questions[section].length;
    const answeredQuestions = Object.keys(responses[section]).length;
    return Math.round(answeredQuestions / totalQuestions * 100);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAllSectionsCompleted()) {
      setError('Please answer all questions in all sections before submitting.');
      return;
    }
    onSubmit(responses);
  };
  if (loading) {
    return <div className="flex flex-col items-center justify-center py-12" data-id="element-125">
        <Spinner size="lg" color="blue" data-id="element-126" />
        <p className="mt-4 text-gray-600" data-id="element-127">
          Generating your personalized questionnaire...
        </p>
        <p className="text-sm text-gray-500 mt-2" data-id="element-128">
          This may take a moment as we analyze your business context.
        </p>
      </div>;
  }
  if (error) {
    return <div className="bg-red-50 border-l-4 border-red-500 p-4" data-id="element-129">
        <p className="text-red-700" data-id="element-130">{error}</p>
        <button className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" onClick={() => window.location.reload()} data-id="element-131">
          Try Again
        </button>
      </div>;
  }
  return <div data-id="element-132">
      <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-133">
        Business Process Self-Assessment
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-134">
        Please rate your business across these 5 key functional areas. Your
        answers will help us identify strengths and opportunities for
        improvement.
      </p>
      <div className="mb-8" data-id="element-135">
        <div className="flex overflow-x-auto pb-2" data-id="element-136">
          {sections.map(section => {
          const completionPercentage = getSectionCompletionPercentage(section.id);
          const isActive = activeSection === section.id;
          return <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex-shrink-0 mr-2 px-4 py-2 rounded-md flex flex-col items-center transition-colors ${isActive ? `bg-${section.color}-100 border-b-2 border-${section.color}-600` : 'bg-gray-50 hover:bg-gray-100'}`} data-id="element-137">
                <div className="flex items-center" data-id="element-138">
                  <span className={`font-medium ${isActive ? `text-${section.color}-700` : 'text-gray-700'}`} data-id="element-139">
                    {section.label}
                  </span>
                  <div className="ml-2 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-140">
                    <div className={`h-4 w-4 rounded-full ${completionPercentage === 100 ? `bg-${section.color}-500` : 'bg-gray-300'}`} data-id="element-141"></div>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2" data-id="element-142">
                  <div className={`h-1.5 rounded-full bg-${section.color}-500`} style={{
                width: `${completionPercentage}%`
              }} data-id="element-143"></div>
                </div>
              </button>;
        })}
        </div>
      </div>
      {questions && questions[activeSection] && <form onSubmit={handleSubmit} data-id="element-144">
          <div className="space-y-8" data-id="element-145">
            {questions[activeSection].map((question, index) => <div key={question.id} className="bg-white border rounded-lg p-4 shadow-sm" data-id="element-146">
                <div className="flex items-start" data-id="element-147">
                  <div className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 mr-3" data-id="element-148">
                    {index + 1}
                  </div>
                  <div className="flex-grow" data-id="element-149">
                    <h3 className="font-medium text-gray-800 mb-3" data-id="element-150">
                      {question.text}
                    </h3>
                    <div className="flex flex-wrap items-center mt-2" data-id="element-151">
                      <div className="w-full md:w-auto flex flex-wrap justify-between md:justify-start" data-id="element-152">
                        {[1, 2, 3, 4, 5].map(rating => <div key={rating} className="mb-2 md:mb-0 md:mr-4 flex items-center" data-id="element-153">
                            <input type="radio" id={`${question.id}-${rating}`} name={question.id} value={rating} checked={responses[activeSection][question.id] === rating} onChange={() => handleResponseChange(activeSection, question.id, rating)} className="h-4 w-4 text-blue-600 focus:ring-blue-500" data-id="element-154" />
                            <label htmlFor={`${question.id}-${rating}`} className="ml-2 text-sm text-gray-700" data-id="element-155">
                              {rating === 1 && 'Never'}
                              {rating === 2 && 'Rarely'}
                              {rating === 3 && 'Sometimes'}
                              {rating === 4 && 'Often'}
                              {rating === 5 && 'Always'}
                            </label>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
          <div className="mt-8 flex justify-between" data-id="element-156">
            <div className="flex items-center" data-id="element-157">
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-158">
                <div className="h-2.5 rounded-full bg-blue-600" style={{
              width: `${sections.reduce((acc, section) => acc + getSectionCompletionPercentage(section.id), 0) / sections.length}%`
            }} data-id="element-159"></div>
              </div>
              <span className="ml-2 text-sm text-gray-600" data-id="element-160">
                {Math.round(sections.reduce((acc, section) => acc + getSectionCompletionPercentage(section.id), 0) / sections.length)}
                % Complete
              </span>
            </div>
            <div className="flex space-x-3" data-id="element-161">
              {activeSection !== 'sales' && <button type="button" onClick={() => {
            const currentIndex = sections.findIndex(s => s.id === activeSection);
            if (currentIndex > 0) {
              setActiveSection(sections[currentIndex - 1].id);
            }
          }} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-162">
                  Previous
                </button>}
              {activeSection !== 'finance' ? <button type="button" onClick={() => {
            const currentIndex = sections.findIndex(s => s.id === activeSection);
            if (currentIndex < sections.length - 1) {
              setActiveSection(sections[currentIndex + 1].id);
            }
          }} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-163">
                  Next
                </button> : <button type="submit" disabled={!isAllSectionsCompleted()} className={`px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${!isAllSectionsCompleted() ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-164">
                  Submit Audit
                </button>}
            </div>
          </div>
        </form>}
    </div>;
};